/**
 * One question, as an owner sees it.
 *
 * The console's own `ApprovalCardView` is still there and still right for the
 * developer view: it shows the tool key, the effect row, the handle, the turn
 * and the holder's own sentence, because somebody debugging a hold needs all
 * five. None of them may appear here. What this card shows is the four things
 * `ask.ts` folded -- what it wants, why it needs a person, the concrete thing,
 * and what the buttons do -- and the answer goes back through exactly the same
 * call the developer view uses (§7.135: pressing a button submits an ordinary
 * input; the asking policy settles the hold).
 *
 * The full request collapses only when the recorded decision arrives. A click
 * alone leaves the question open, and a recorded answer does not assert execution.
 */

import { useEffect, useState } from "react";
import "./AskCard.css";
import { api, ApiError, type ApprovalCard } from "../lib/api";
import { Card, Details, Lines, Pill } from "../components/owner";
import { Icon } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";
import { approvalResolution, ownerAsk, type OwnerAsk, type ToolIndex } from "./ask";
import { failureWords, whenWords } from "./words";
import { ConfigurationChanges, configurationChanges, configurationResets } from "./ConfigurationChanges";

export function AskCard({
  card,
  tools,
  onToast,
  onDone,
  disabled = false,
  onAnswer,
  presentation,
  agentId,
}: {
  card: ApprovalCard;
  agentId?: string;
  tools: ToolIndex;
  onToast: (message: string, tone: "good" | "critical") => void;
  onDone: () => void;
  disabled?: boolean;
  /** Global Studio uses the same endpoint scoped to this card's Agent. */
  onAnswer?: (key: string, decision: string) => Promise<unknown>;
  /** Extra readable context, supplied only after the caller matches this exact effect. */
  presentation?: Pick<OwnerAsk, "title" | "why" | "what">;
}) {
  // The option being sent, then the one sent, until the record says so: the
  // card's `resolution` is the answer's own row on the record (§7.194 ④ ⑤),
  // and it is that -- not this request returning -- that puts the buttons
  // down for good. A second press in the gap would be a second answer.
  const [busy, setBusy] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  useEffect(() => { if (card.resolution || card.resolved) setSent(null); }, [card.resolution, card.resolved]);
  const [failure, setFailure] = useState<string | null>(null);
  const original = ownerAsk(card, tools);
  const ask = presentation ? { ...original, ...presentation } : original;
  const changes = configurationChanges(card, tools);
  const resets = configurationResets(card, tools);
  const resolved = card.resolved;
  const resolution = approvalResolution(card);
  const result = card.command_result;
  const execution = result?.action_state;
  const resolvedTime = tsOf(card.resolved_at);
  const when = resolved && resolvedTime !== null
    ? `结束于 ${whenWords(resolvedTime)}` : whenWords(tsOf(card.ts));

  const answer = async (value: string, label: string) => {
    if (resolved || disabled || busy !== null || sent !== null || card.resolution) return;
    setBusy(value);
    setFailure(null);
    try {
      await (onAnswer ?? api.resolveApproval)(card.key, value);
      setSent(value);
      onToast(`答复「${label}」已送出，正在核对结果`, "good");
      onDone();
    } catch (err) {
      const failed = ApiError.fromUnknown(err);
      setFailure(failed.detail || failed.title);
      onToast(`回答没送出去：${failed.detail}`, "critical");
    } finally {
      setBusy(null);
    }
  };

  const body = (<>
    {disabled && !resolved ? <p className="own-why">这项请求暂时无法处理，请回到原对话核对最新状态。</p> : null}
    {changes ? <ConfigurationChanges agentId={agentId} changes={changes} resets={resets} tools={tools} /> : ask.what.length ? (
      <Lines
        rows={ask.what.map((line) => ({
          label: line.label,
          value: line.value,
          quoted: line.quoted,
        }))}
      />
    ) : ask.unnamedTool ? (
      /* Nothing declared anywhere: no argument a schema names, and no sentence
         from the tool -- not even the headline, which is the category's own
         words in that case. This is the sentence that says so, and the finding
         that says which plugin owes a description. A card whose *title* is the
         declared act needs no such line and does not get one (D665). */
      <p>
        暂时没有可读的操作摘要，请先核对具体请求。当前还缺少这项操作的说明。
      </p>
    ) : null}


    {failure ? (
      <p className="own-why" role="alert" data-testid="ask-failed">
        <Icon name="alert" size={15} />
        <span>这个回答没送出去：{failure}</span>
      </p>
    ) : null}

    <Details label="细节">
      <Lines
        rows={[
          ...(presentation ? original.what.map(line => ({ label: line.label, value: line.value, quoted: line.quoted })) : []),
          ...ask.detail.map((line) => ({
            label: line.label,
            value: line.value,
            quoted: line.quoted,
          })),
          ...(!changes && ask.unnamed > 0
            ? [
                {
                  label: "没说完的",
                  value: `这次请求还有 ${ask.unnamed} 项未包含在摘要中。`,
                },
              ]
            : []),
        ]}
      />
    </Details>
  </>);

  const executionText = execution === "completed" ? result?.source === "setup_claim" ? "这次认领已生效。" : "这次处理已结束，结果见原任务。"
    : execution === "failed" ? "原请求未能完成。"
      : execution === "stopped" ? "原请求已停下。"
        : execution === "held" ? "原请求仍在等待确认。"
          : execution === "running" ? "原请求仍在处理中。"
            : execution === "unknown" ? "原请求结果尚不确定。" : "原请求执行结果尚未读到。";

  if (resolved) return (
    <article className="own-card pa-open-group own-ask-result" data-tone={execution === "failed" ? "bad" : "neutral"}>
      <details className="own-ask-result-details">
        <summary>
          <span className="own-ask-result-heading">
            <span className="own-ask-result-title">{ask.title}</span>
            <Pill tone="neutral">{resolution.label}</Pill>
          </span>
          <span className="ask-execution" role={execution === "failed" ? "alert" : undefined}>{executionText}</span>
          <span className="own-ask-result-toggle">查看原请求与答复 <Icon name="down" size={12} /></span>
        </summary>
        <div className="own-details-body">
          <time className="own-when">{when}</time>
          {body}
        </div>
      </details>
      {result?.error ? <p className="micro" role="alert">{failureWords(result.error)}</p> : null}
    </article>
  );

  return (
    <Card
      className="own-ask"
      tone="waiting"
      title={ask.title}
      when={when}
      badge={<Pill tone="waiting">{resolution.label}</Pill>}
      actions={
        <div className="own-choice">
          {ask.options.map((option, i) => (
            <button
              key={option.value}
              type="button"
              // One primary on the card: the first option that goes ahead.
              className={`btn${i === ask.options.findIndex(item => item.approves) ? " btn-accent" : ""}`}
              disabled={disabled || busy !== null || sent !== null || Boolean(card.resolution)}
              aria-busy={busy === option.value || sent === option.value || card.resolution === option.value}
              onClick={() => void answer(option.value, option.label)}
            >
              {busy === option.value ? <LightMotion compact state="working" />
                : sent === option.value || card.resolution === option.value ? <LightMotion compact state="waiting" />
                : <Icon name={option.approves ? "yes" : "no"} size={15} />}
              {busy === option.value ? "正在送出…" : option.label}
              {i === 0 ? <span className="visually-hidden">，提交这个答复</span> : null}
            </button>
          ))}
        </div>
      }
    >
      {body}
    </Card>
  );
}

/** The card's `ts` is a float or a string, depending on who wrote the row. */
function tsOf(ts: number | string | null | undefined): number | null {
  if (ts == null) return null;
  if (typeof ts === "number") return Number.isFinite(ts) ? ts : null;
  const parsed = Number(ts);
  return Number.isFinite(parsed) ? parsed : null;
}
